import { useEffect, useState } from 'react';
import type { Tenant } from '@etiquetachef/types';
import {
  subscribeTenants,
  type TenantListFilters,
  type TenantListResult,
} from '@/lib/firestoreTenants';

export type UseTenantsState =
  | { kind: 'LOADING' }
  | { kind: 'READY'; tenants: Tenant[]; truncated: boolean }
  | { kind: 'ERROR'; error: unknown };

/**
 * Mantém a lista de tenants assinada em tempo real (`onSnapshot`).
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26 16:49 +0200 (lista de
 * tenants com filtro + busca + listener real-time, Sprint 2).
 *
 * Por quê: os seletores de empresa das telas de finanças precisam da lista
 * viva e barata; a lista principal de Empresas migrou para a busca no
 * servidor (`useTenantsSearch`, #71) e não usa mais este hook.
 *
 * Contrato: `LOADING` → `READY` (`tenants` + `truncated` quando bateu o teto
 * `TENANTS_LIST_LIMIT` = 200) | `ERROR`. Trocar `filters` (por identidade —
 * memoize no chamador) ou `refreshNonce` descarta o listener anterior e abre
 * outro (single-flight). O cleanup cancela o listener.
 *
 * Usado por: `routes/InvoicesPage.tsx`, `routes/PaymentsPage.tsx`,
 * `features/finance/RequestRefundDialog.tsx`.
 *
 * Dados: `tenants` ordenado por `createdAt desc`, com `where('status','==',…)`
 * opcional — índice composto `status + createdAt` em
 * `firebase/firestore.indexes.json`. Detalhe em `subscribeTenants`
 * (`lib/firestoreTenants.ts`).
 *
 * Dicas: `filters` criado inline a cada render reabre o listener em loop —
 * passe um objeto estável (`useMemo`).
 */
export function useTenants(
  filters: TenantListFilters,
  /** Mudar este valor força re-subscribe (botão "Atualizar"). */
  refreshNonce?: number,
): UseTenantsState {
  const [state, setState] = useState<UseTenantsState>({ kind: 'LOADING' });

  useEffect(() => {
    setState({ kind: 'LOADING' });
    const unsubscribe = subscribeTenants(
      filters,
      (result: TenantListResult) => {
        setState({ kind: 'READY', tenants: result.tenants, truncated: result.truncated });
      },
      (error) => {
        setState({ kind: 'ERROR', error });
      },
    );
    return unsubscribe;
  }, [filters, refreshNonce]);

  return state;
}
